/**
 * map_file_load — fetch the baked building map once per seed (2026-09-24). Shared by the World Atlas's structure
 * layer and the local map (M), so the 2.7 MB file is fetched and parsed once whichever opens first.
 * `public/maps/<fnv(seed)>.json` (verify/bake-proxies.mts --map-only); a missing or foreign file resolves to null.
 */
import { mapFileFor, mapFileProblem, type MapFile } from './map_structures.js';

const pending = new Map<string, Promise<MapFile | null>>();
const loaded = new Map<string, MapFile | null>();

/** The file for a seed once it has loaded (null while loading or when there is none). */
export function loadedMapFile(seed: string): MapFile | null { return loaded.get(seed) ?? null; }

/** A TRANSIENT failure (network blip, 5xx, a dev-server restart) is retried after these waits (ms), then given up.
 *  2026-09-25: one failed fetch was latched for the seed for the whole session — building zoom empty and the Find tab
 *  "buildings not baked for this world" until a reload. A 404 or a file that fails validation stays final. */
const RETRY_MS = [2_000, 6_000, 18_000];

type Attempt = { final: true; file: MapFile | null } | { final: false; why: unknown };

async function fetchMapFileOnce(seed: string): Promise<Attempt> {
  let res: Response;
  try { res = await fetch(`/maps/${mapFileFor(seed)}`); } catch (e) { return { final: false, why: e }; }
  if (res.status === 404) return { final: true, file: null }; // not baked for this world: final
  if (!res.ok) return { final: false, why: `HTTP ${res.status}` };
  let json: unknown;
  try { json = await res.json(); } catch (e) {
    if (e instanceof SyntaxError) { console.warn(`[maps] ignoring ${mapFileFor(seed)}: not JSON`); return { final: true, file: null }; }
    return { final: false, why: e }; // the body broke off mid-transfer
  }
  const problem = mapFileProblem(json, seed);
  if (problem) { console.warn(`[maps] ignoring ${mapFileFor(seed)}: ${problem}`); return { final: true, file: null }; }
  return { final: true, file: json as MapFile };
}

/** Fetch + validate once per seed (idempotent; concurrent callers share the promise). */
export function loadMapFile(seed: string): Promise<MapFile | null> {
  let p = pending.get(seed);
  if (p) return p;
  p = (async (): Promise<MapFile | null> => {
    for (let attempt = 0; ; attempt++) {
      const r = await fetchMapFileOnce(seed);
      if (r.final) { loaded.set(seed, r.file); return r.file; }
      if (attempt >= RETRY_MS.length) {
        console.warn(`[maps] the building map failed to load after ${attempt + 1} tries — giving up`, r.why);
        loaded.set(seed, null);
        return null;
      }
      console.warn(`[maps] the building map failed to load — retrying in ${RETRY_MS[attempt] / 1000} s`, r.why);
      await new Promise<void>((done) => { setTimeout(done, RETRY_MS[attempt]); });
    }
  })();
  pending.set(seed, p);
  return p;
}
